import React from 'react';
import { ImageDetail } from '@/components/image-lightbox';
import { ProfilePictureSidebar } from '@/components/image-lightbox/sidebars';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { LightboxImage } from '@/components/image-lightbox';

interface ProfileAvatarLightboxProps {
  currentImage: LightboxImage | null;
  isLightboxOpen: boolean;
  lightboxImages: LightboxImage[];
  showDeleteConfirmation: boolean;
  isSettingProfilePic: boolean;
  isDeleting: boolean;
  isCurrentImageActiveProfile: boolean;
  profilePictureUrl?: string | null;
  onClose: () => void;
  onNavigate: (direction: 'next' | 'previous') => void;
  onChooseProfilePicture: () => Promise<void>;
  onDeleteClick: () => void;
  onConfirmDelete: () => Promise<void>;
  onCancelDelete: () => void;
  onRemoveClick?: () => void;
}

const ProfileAvatarLightbox: React.FC<ProfileAvatarLightboxProps> = ({
  currentImage,
  isLightboxOpen,
  lightboxImages,
  showDeleteConfirmation,
  isSettingProfilePic,
  isDeleting,
  isCurrentImageActiveProfile,
  profilePictureUrl,
  onClose,
  onNavigate,
  onChooseProfilePicture,
  onDeleteClick,
  onConfirmDelete,
  onCancelDelete,
  onRemoveClick
}) => {
  // Convert direction-based navigation to image-based for ImageDetail
  const handleImageDetailNavigate = (image: LightboxImage) => {
    const currentIndex = lightboxImages.findIndex(img => img.url === currentImage?.url);
    const targetIndex = lightboxImages.findIndex(img => img.url === image.url);
    
    if (targetIndex > currentIndex) {
      onNavigate('next');
    } else if (targetIndex < currentIndex) {
      onNavigate('previous');
    }
  };

  return (
    <>
      {/* Confirmation Dialog for Profile Picture Deletion */}
      <ConfirmDialog
        isOpen={showDeleteConfirmation}
        onConfirm={onConfirmDelete}
        onCancel={onCancelDelete}
        title="Delete Image"
        description={isCurrentImageActiveProfile 
          ? "Are you sure you want to delete your current profile picture? This action cannot be undone." 
          : "Are you sure you want to delete this image from your collection? This action cannot be undone."}
        confirmText={isDeleting ? "Deleting..." : "Delete Image"}
        cancelText="Keep Image"
        confirmVariant="destructive"
        cancelVariant="green"
      />
      
      {/* Universal Image Detail with Profile Picture Sidebar */}
      {currentImage && (
        <ImageDetail<LightboxImage>
          image={currentImage}
          isOpen={isLightboxOpen}
          onClose={onClose}
          allImages={lightboxImages}
          onNavigate={handleImageDetailNavigate}
          getImageUrl={(item) => item.url}
          getImageId={(item) => item.id}
          getImageDescription={(item) => item.alt}
          getUserId={() => undefined} // Profile pictures don't have user ownership
          getFavoriteCount={() => undefined} // Profile pictures don't have favorites
          showFavoriteButton={false} // Hide favorites for profile pictures
          showDescription={false} // Hide description for profile pictures
          config={{
            preloading: { enabled: false, strategy: 'conservative', preloadDistance: 0 },
            navigation: { wheel: true, keyboard: true, clickZones: true },
            mobile: { fullscreen: true, sidebarPosition: 'bottom' }
          }}
          renderSidebar={(item, context) => (
            <ProfilePictureSidebar
              context={context}
              isSettingProfilePic={isSettingProfilePic}
              isCurrentImageActiveProfile={item.url === profilePictureUrl}
              onChooseProfilePicture={onChooseProfilePicture}
              onDeleteClick={onDeleteClick}
              onRemoveClick={onRemoveClick}
            />
          )}
        />
      )}
    </>
  );
};

export default ProfileAvatarLightbox;
